import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Package, Truck, Home, CheckCircle, ShoppingCart, ChevronDown, ChevronUp, LogOut, AlertOctagon, BookOpen, FileDown } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import UpcomingEventsAlertPanel from "@/components/UpcomingEventsAlertPanel";
import { PromptModal } from "@/components/AppDialogs";
import { toast } from "sonner";

const STATUS_CONFIG = {
  offert:  { label: "Offert",  Icon: ShoppingCart, color: "#FF8A00", next: "achete",  nextLabel: "Marquer acheté",   role: "operations" },
  achete:  { label: "Acheté",  Icon: Package,      color: "#D81B8C", next: "stocke",  nextLabel: "Marquer stocké",   role: "logistics"  },
  stocke:  { label: "Stocké",  Icon: Package,      color: "#6B2C8F", next: "remis",   nextLabel: "Remis à l'événement",  role: "logistics"  },
  remis:   { label: "Remis",   Icon: CheckCircle,  color: "#3FA535", next: "livre",   nextLabel: "Livré à domicile", role: "logistics"  },
  livre:   { label: "Livré",   Icon: Home,         color: "#206F18", next: null,      nextLabel: null,                role: null         },
  bloque:  { label: "Bloqué",  Icon: AlertOctagon, color: "#DC2626", next: null,      nextLabel: null,                role: null         },
};

const QUEUES = ["offert", "achete", "stocke", "remis", "livre", "bloque"];

const ROLE_QUEUES = {
  operations: ["offert", "achete", "stocke", "remis", "livre", "bloque"],
  logistics:  ["achete", "stocke", "remis", "livre", "bloque"],
};

export default function AdminOperations() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const role = user?.role;
  const isSuperAdmin = role === "super_admin" || role === "admin";
  const visibleQueues = isSuperAdmin ? QUEUES : (ROLE_QUEUES[role] || QUEUES);
  const [activeQueue, setActiveQueue] = useState(visibleQueues[0] || "offert");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [transitioning, setTransitioning] = useState(null);
  const [transitionPromptFor, setTransitionPromptFor] = useState(null); // { order, toStatus }
  const [issuePromptFor, setIssuePromptFor] = useState(null); // order
  // Q4: group the "Offert" queue by supplier so Ops can plan a purchase trip
  // (one visit to Fournisseur X = N gifts in one shot). Toggle persists in
  // localStorage so it feels stable across sessions.
  const [groupBySupplier, setGroupBySupplier] = useState(() => {
    try { return localStorage.getItem("milkado_ops_group_by_supplier") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("milkado_ops_group_by_supplier", groupBySupplier ? "1" : "0"); }
    catch (_) { /* ignore */ }
  }, [groupBySupplier]);
  const [expandedSupplier, setExpandedSupplier] = useState({}); // { supplierName: bool }
  const [bulkBusy, setBulkBusy] = useState(false);

  const isLogistics = role === "logistics";
  const isOperations = role === "operations";
  // For super_admin/admin (who has no operational role), fall back to the URL
  // pathname so that /admin/logistics shows "Logistique" and /admin/operations
  // shows "Opérations" — no combined title anywhere.
  const pageTitle = isLogistics
    ? "Logistique"
    : isOperations
      ? "Opérations"
      : (typeof window !== "undefined" && window.location.pathname.includes("/admin/logistics")
          ? "Logistique"
          : "Opérations");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/gift-orders?status=${activeQueue}`);
      setOrders(data);
    } catch (e) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // refresh() closes over `activeQueue` and `api` (module-level singleton),
  // so only `activeQueue` needs to trigger a re-fetch.
  useEffect(() => {
    refresh();
  }, [activeQueue]);

  const transition = (order, toStatus) => {
    setTransitionPromptFor({ order, toStatus });
  };

  const submitTransition = async (note) => {
    const req = transitionPromptFor;
    setTransitionPromptFor(null);
    if (!req) return;
    setTransitioning(req.order.id);
    try {
      await api.patch(`/admin/gift-orders/${req.order.id}/transition`, {
        to_status: req.toStatus,
        note: note || "",
      });
      refresh();
      toast.success("Transition enregistrée.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Transition refusée.");
    } finally {
      setTransitioning(null);
    }
  };

  const signalIssue = (order) => {
    setIssuePromptFor(order);
  };

  const submitIssue = async (reason) => {
    const order = issuePromptFor;
    setIssuePromptFor(null);
    if (!order) return;
    setTransitioning(order.id);
    try {
      await api.post(`/admin/gift-orders/${order.id}/signal-issue`, { reason });
      toast.success(
        "Problème signalé. Le commercial responsable de l'événement reçoit l'alerte et tranchera.",
      );
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Signalement refusé.");
    } finally {
      setTransitioning(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Group `orders` by supplier_name (used in the "Par fournisseur" view).
  const supplierGroups = React.useMemo(() => {
    const map = new Map();
    orders.forEach((o) => {
      const key = o.supplier_name || "— Fournisseur non défini —";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    });
    return Array.from(map.entries())
      .map(([name, items]) => {
        const first = items[0] || {};
        return {
          name,
          items,
          contact: first.supplier_contact || "",
          totalCost: items.reduce((sum, x) => sum + (x.supplier_cost || 0), 0),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [orders]);

  const bulkMarkPurchased = async (items) => {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      const canAct = (o) => o.workflow_status === "offert" && (isSuperAdmin || role === "operations");
      const targets = items.filter(canAct);
      if (targets.length === 0) {
        toast.error("Aucun cadeau éligible dans ce groupe.");
        return;
      }
      let ok = 0;
      for (const o of targets) {
        try {
          await api.patch(`/admin/gift-orders/${o.id}/transition`, {
            to_status: "achete",
            note: "Achat groupé par fournisseur",
          });
          ok += 1;
        } catch (_) { /* per-item error swallowed, keep going */ }
      }
      toast.success(`${ok}/${targets.length} cadeau(x) marqué(s) « acheté ».`);
      refresh();
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadSupplierPdf = async (supplierName) => {
    try {
      const resp = await api.get(
        `/admin/purchase-orders/pdf?supplier_name=${encodeURIComponent(supplierName)}`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const slug = (supplierName || "fournisseur").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `milkado-bon-${slug}-${ymd}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Bon fournisseur téléchargé — ${supplierName}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Téléchargement PDF impossible.");
    }
  };

  const queueCounts = {};
  // Fetch counts in parallel for the tab badges (lightweight side-call)
  useEffect(() => {
    Promise.all(
      visibleQueues.map((s) =>
        api.get(`/admin/gift-orders?status=${s}`).then((r) => [s, r.data.length]).catch(() => [s, 0]),
      ),
    ).then((entries) => {
      const next = Object.fromEntries(entries);
      // Force a state update by storing on document body data attribute (simple cache).
      document.dispatchEvent(new CustomEvent("milkado-ops-counts", { detail: next }));
    });
  }, [activeQueue, visibleQueues]);

  const [counts, setCounts] = useState({});
  useEffect(() => {
    const handler = (e) => setCounts(e.detail);
    document.addEventListener("milkado-ops-counts", handler);
    return () => document.removeEventListener("milkado-ops-counts", handler);
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24" data-testid="admin-operations-page">
      <TopBar />
      <div className="max-w-6xl mx-auto px-5 pt-6">
        {isSuperAdmin && (
          <button
            onClick={() => navigate("/admin")}
            data-testid="ops-back"
            className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C] mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Tableau de bord
          </button>
        )}

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D81B8C] mb-2">
              Workflow cadeaux
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C]">
              {pageTitle}
            </h1>
            <p className="mt-2 text-sm text-[#5C4664]">
              Connecté en tant que <strong>{user?.email}</strong> — rôle <strong>{role}</strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="ops-logout"
              className="rounded-full border-[#F2E2EC] gap-2"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </Button>
            {(isOperations || isSuperAdmin) && (
              <Button
                variant="outline"
                onClick={() => navigate("/admin/catalog")}
                data-testid="ops-catalog-link"
                className="rounded-full border-[#F2E2EC] gap-2"
                title="Catalogue produits — lecture, création, modification, archivage"
              >
                <BookOpen className="w-4 h-4" />
                Catalogue produits
              </Button>
            )}
          </div>
        </div>

        {/* Read-only upcoming events panel with logistics alerts */}
        <div className="mt-8">
          <UpcomingEventsAlertPanel readOnly />
        </div>

        {/* Queue tabs */}
        <div className="mt-2 flex gap-2 overflow-x-auto pb-2" data-testid="ops-tabs">
          {visibleQueues.map((s) => {
            const cfg = STATUS_CONFIG[s];
            const active = activeQueue === s;
            return (
              <button
                key={s}
                data-testid={`ops-tab-${s}`}
                onClick={() => { setActiveQueue(s); setExpanded(null); }}
                className={`flex items-center gap-2 px-4 h-11 rounded-full text-sm font-semibold transition-all border ${
                  active
                    ? "text-white border-transparent shadow-md"
                    : "bg-white text-[#5C4664] border-[#F2E2EC] hover:border-[#D81B8C]/50"
                }`}
                style={active ? { backgroundColor: cfg.color } : {}}
              >
                <cfg.Icon className="w-4 h-4" />
                {cfg.label}
                {counts[s] !== undefined && (
                  <span
                    className={`ml-1 px-1.5 h-5 rounded-full text-[10px] font-bold flex items-center ${
                      active ? "bg-white/25" : "bg-[#FFF5FA] text-[#D81B8C]"
                    }`}
                  >
                    {counts[s]}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Orders list */}
        <div className="mt-6">
          {/* Q4 — Group-by-supplier toggle: only on the "Offert" queue, since
              that's the shopping-list phase where a supplier trip = 1 visit
              for multiple gifts. */}
          {activeQueue === "offert" && orders.length > 0 && (
            <div className="mb-4 flex items-center justify-end gap-2" data-testid="ops-view-toggle">
              <button
                onClick={() => setGroupBySupplier(false)}
                data-testid="ops-view-list"
                className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                  !groupBySupplier
                    ? "bg-[#2A0E3C] text-white border-[#2A0E3C]"
                    : "bg-white text-[#5C4664] border-[#F2E2EC] hover:border-[#D81B8C]"
                }`}
              >
                Liste
              </button>
              <button
                onClick={() => setGroupBySupplier(true)}
                data-testid="ops-view-supplier"
                className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1.5 ${
                  groupBySupplier
                    ? "bg-[#2A0E3C] text-white border-[#2A0E3C]"
                    : "bg-white text-[#5C4664] border-[#F2E2EC] hover:border-[#D81B8C]"
                }`}
              >
                <Package className="w-3.5 h-3.5" />
                Par fournisseur
              </button>
            </div>
          )}

          {loading ? (
            <p className="text-sm text-[#5C4664] py-10 text-center">Chargement…</p>
          ) : orders.length === 0 ? (
            <div
              data-testid="ops-empty"
              className="py-12 text-center text-[#5C4664] bg-white rounded-2xl border border-[#F2E2EC]"
            >
              Aucun cadeau dans cette file.
            </div>
          ) : (activeQueue === "offert" && groupBySupplier) ? (
            <ul className="space-y-4" data-testid="ops-supplier-groups">
              {supplierGroups.map((g) => {
                const expanded = expandedSupplier[g.name] !== false; // default open
                return (
                  <li
                    key={g.name}
                    data-testid={`ops-supplier-group-${g.name}`}
                    className="bg-white rounded-2xl border border-[#F2E2EC]"
                  >
                    <div className="p-4 sm:p-5 flex items-center gap-3 flex-wrap">
                      <button
                        onClick={() =>
                          setExpandedSupplier((s) => ({ ...s, [g.name]: expanded ? false : true }))
                        }
                        data-testid={`ops-supplier-toggle-${g.name}`}
                        className="w-9 h-9 rounded-full border border-[#F2E2EC] hover:border-[#D81B8C] text-[#5C4664] hover:text-[#D81B8C] flex items-center justify-center shrink-0"
                      >
                        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-[#2A0E3C] flex items-center gap-2 flex-wrap">
                          <span className="text-lg">🏢 {g.name}</span>
                          <span className="text-xs font-normal text-[#5C4664] bg-[#FCE7F3] text-[#D81B8C] px-2 py-0.5 rounded-full">
                            {g.items.length} cadeau(x)
                          </span>
                        </div>
                        {g.contact && (
                          <a
                            href={/^\+?\d/.test(g.contact) ? `tel:${g.contact.replace(/\s/g, "")}` : `mailto:${g.contact}`}
                            className="text-xs text-[#5C4664] hover:text-[#D81B8C] underline decoration-dotted"
                            data-testid={`ops-supplier-contact-${g.name}`}
                          >
                            {g.contact}
                          </a>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] text-[#5C4664] uppercase tracking-widest">
                          Total à débourser
                        </div>
                        <div className="font-display text-lg font-black text-[#D81B8C]">
                          ${g.totalCost.toFixed(0)}
                        </div>
                      </div>
                      {(isSuperAdmin || role === "operations") && (
                        <>
                          <Button
                            variant="outline"
                            onClick={() => downloadSupplierPdf(g.name)}
                            disabled={g.name.startsWith("—")}
                            data-testid={`ops-supplier-pdf-${g.name}`}
                            className="rounded-full h-9 px-4 text-xs font-semibold border-[#F2E2EC] hover:border-[#6B2C8F] hover:text-[#6B2C8F] gap-1.5 disabled:opacity-50"
                            title={g.name.startsWith("—")
                              ? "Bon indisponible : renseignez d'abord le fournisseur sur les produits concernés."
                              : `Télécharger le bon de commande PDF pour ${g.name}`}
                          >
                            <FileDown className="w-3.5 h-3.5" />
                            Bon PDF
                          </Button>
                          <Button
                            onClick={() => bulkMarkPurchased(g.items)}
                            disabled={bulkBusy}
                            data-testid={`ops-supplier-bulk-${g.name}`}
                            className="rounded-full h-9 px-4 text-xs font-semibold bg-[#D81B8C] hover:bg-[#B21571] text-white gap-1.5 disabled:opacity-60"
                            title={`Marquer tous les ${g.items.length} cadeau(x) comme achetés chez ${g.name}`}
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                            Tout marquer acheté
                          </Button>
                        </>
                      )}
                    </div>
                    {expanded && (
                      <ul className="border-t border-[#F2E2EC]">
                        {g.items.map((o) => (
                          <li
                            key={o.id}
                            data-testid={`ops-supplier-order-${o.id}`}
                            className="p-4 flex items-center gap-3 flex-wrap border-b border-[#F2E2EC] last:border-b-0"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-[#2A0E3C] truncate">
                                {o.product_name || "Produit supprimé"}
                              </div>
                              <div className="text-xs text-[#5C4664] mt-0.5">
                                {o.event_title} ({o.event_code}) · {o.participants?.length || 0} contributeur(s)
                              </div>
                            </div>
                            <div className="text-right shrink-0 text-sm">
                              <div className="font-bold text-[#2A0E3C]">${(o.total_amount || 0).toFixed(0)}</div>
                              <div className="text-[10px] text-[#5C4664]">coût ${(o.supplier_cost || 0).toFixed(0)}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <ul className="space-y-3">
              {orders.map((o) => {
                const cfg = STATUS_CONFIG[o.workflow_status];
                const canAct =
                  cfg?.next &&
                  (isSuperAdmin ||
                    role === cfg.role) &&
                  !(o.workflow_status === "remis" && !o.home_delivery); // hide livre transition if no home_delivery
                return (
                  <li
                    key={o.id}
                    data-testid={`ops-order-${o.id}`}
                    className="bg-white rounded-2xl border border-[#F2E2EC] p-4 sm:p-5"
                  >
                    <div className="flex items-start gap-4 flex-wrap">
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}
                      >
                        <cfg.Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-[#2A0E3C] truncate">
                          {o.product_name || "Produit supprimé"}
                        </div>
                        <div className="text-xs text-[#5C4664] mt-0.5">
                          Événement <strong>{o.event_title}</strong> ({o.event_code})
                          {" · "}
                          {o.source_type === "individual" && "Achat individuel"}
                          {o.source_type === "group" && "Cagnotte"}
                          {o.source_type === "last_minute" && "Dernière minute"}
                          {" · "}
                          {o.participants?.length || 0} contributeur(s)
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display font-black text-[#D81B8C]">
                          ${(o.total_amount || 0).toFixed(0)}
                        </div>
                        <div className="text-[10px] text-[#5C4664]">
                          coût ${(o.supplier_cost || 0).toFixed(0)}
                        </div>
                      </div>
                      <button
                        onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                        data-testid={`ops-expand-${o.id}`}
                        className="w-8 h-8 rounded-full border border-[#F2E2EC] hover:border-[#D81B8C] flex items-center justify-center text-[#5C4664]"
                      >
                        {expanded === o.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>

                    {expanded === o.id && (
                      <div
                        data-testid={`ops-order-details-${o.id}`}
                        className="mt-4 pt-4 border-t border-[#F2E2EC] grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm"
                      >
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664] mb-2">
                            Fournisseur
                          </div>
                          <div className="text-[#2A0E3C]">{o.supplier_name || "—"}</div>
                          <div className="text-xs text-[#5C4664]">{o.supplier_contact || "—"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664] mb-2">
                            Destination finale
                          </div>
                          <div className="text-[#2A0E3C]">
                            {o.home_delivery ? "Remise au venue puis livraison à domicile" : "Remise au venue uniquement"}
                          </div>
                        </div>
                        <div className="sm:col-span-2">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664] mb-2">
                            Contributeurs
                          </div>
                          <ul className="space-y-1 text-xs">
                            {(o.participants || []).map((p, i) => (
                              <li
                                key={`${p.name || "anon"}-${p.paid_at || i}`}
                                className="flex justify-between text-[#2A0E3C]"
                              >
                                <span>{p.name} · <span className="text-[#5C4664]">{p.phone || "—"}</span></span>
                                <span className="font-bold">${(p.amount || 0).toFixed(0)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="sm:col-span-2">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664] mb-2">
                            Historique
                          </div>
                          <ul className="text-xs text-[#5C4664] space-y-0.5">
                            {(o.status_history || []).map((h, i) => (
                              <li key={`${h.at || i}-${h.to}`}>
                                <span className="font-mono">{new Date(h.at).toLocaleString()}</span>
                                {" → "}
                                <span className="text-[#2A0E3C] font-semibold">{h.to}</span>
                                {h.by_user && ` par ${h.by_user}`}
                                {h.note && ` — ${h.note}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {canAct && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button
                          onClick={() => transition(o, cfg.next)}
                          disabled={transitioning === o.id}
                          data-testid={`ops-advance-${o.id}`}
                          className="rounded-full text-white h-9 px-4 text-sm font-semibold gap-2 disabled:opacity-60"
                          style={{ backgroundColor: STATUS_CONFIG[cfg.next].color }}
                        >
                          {cfg.nextLabel}
                        </Button>
                        {(o.workflow_status === "offert" || o.workflow_status === "achete") && (
                          <Button
                            variant="outline"
                            onClick={() => signalIssue(o)}
                            disabled={transitioning === o.id}
                            data-testid={`ops-signal-${o.id}`}
                            className="rounded-full h-9 px-4 text-sm font-semibold border-red-200 text-red-600 hover:bg-red-50 hover:border-red-400 disabled:opacity-60"
                            title="Signaler une rupture / un problème — le commercial sera alerté."
                          >
                            🚨 Signaler un problème
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <PromptModal
        open={!!transitionPromptFor}
        onClose={() => setTransitionPromptFor(null)}
        onSubmit={submitTransition}
        title="Ajouter une note (optionnel)"
        description={transitionPromptFor ? `${transitionPromptFor.order.product_name} → ${STATUS_CONFIG[transitionPromptFor.toStatus]?.label || transitionPromptFor.toStatus}` : ""}
        placeholder="Ex: reçu en parfait état"
        kind="textarea"
        required={false}
        submitLabel="Valider la transition"
        testId="transition-note-modal"
      />

      <PromptModal
        open={!!issuePromptFor}
        onClose={() => setIssuePromptFor(null)}
        onSubmit={submitIssue}
        title="Signaler un problème"
        description={issuePromptFor ? `Décrivez le problème rencontré sur « ${issuePromptFor.product_name} ». Le commercial recevra l'alerte immédiatement.` : ""}
        placeholder="Ex: rupture de stock chez le fournisseur"
        kind="textarea"
        submitLabel="Envoyer le signalement"
        testId="signal-issue-modal"
      />
    </div>
  );
}
